import * as React from "react";
import { graphql } from "gatsby";
import _ from "lodash";
import { useI18next, useTranslation } from "gatsby-plugin-react-i18next";
import { getImage } from "gatsby-plugin-image";
import { v4 as uuidv4 } from "uuid";
import Page from "components/Page";
import Link from "components/Link";
import Cta from "components/Cta";
import SinglePageTemplate, {
  DataCard,
  DataCardItem,
} from "components/SinglePageTemplate";
import Breadcrumbs from "components/Breadcrumbs";
import { videoPath, teamPath, newsPath, eventsRootPath } from "components/Nav/config";
import RichText from "components/RichText";
import ShareButtons from "components/ShareButtons";
import ResponsivePlayer from "../../components/ResponsivePlayer";
import PostGrid, { PostCard } from "components/PostGrid/PostGrid";

const VideoTemplate = ({ data, location }) => {
  const { t } = useTranslation();
  const { language } = useI18next();

  const ytvideo = data.ytvideo;
  const title = ytvideo[`titulo_${language}`];
  const slug = ytvideo[`slug`];
  const timeDate = ytvideo[`datetime_${language}`];
  const originalDateData = ytvideo.datetime;
  const researchers = ytvideo.ks_people.map((node) => {
    return (
      <li key={uuidv4()}>
        <Link to={`${teamPath}/${node.slug}`}>
          {`${node.preferred_name.given_name} ${node.preferred_name.family_name}`}
        </Link>
      </li>
    );
  });

  const categories = ytvideo.video_categories.map((category) => {
    if(category.priority !== 100){
      return (
        <li key={uuidv4()}>
          <Link to={`${videoPath}/category/${category.slug}`} key={category.id}>
            {category[`nombre_${language}`]}
          </Link>
        </li>
      );
    }
    return (
      <li key={uuidv4()}>
        {category[`nombre_${language}`]}
      </li>
    );
  });
  
  const body = _.get(
    ytvideo,
    `descripcion_${language}`
  );
  console.log(body);
  const YtvideoDataCard = () => (
    <DataCard title={t("video")}>
      <DataCardItem
        title={t("videos.dataCard.publicationDate")}
        data={timeDate}
      />
      {categories.length > 0 && (
        <DataCardItem
          title={t("videos.dataCard.categories")}
          DataComponent="ul"
          data={categories}
        />
      )}
      {researchers.length > 0 && (
        <DataCardItem
          title={t("news.authors")}
          DataComponent="ul"
          data={researchers}
        />
      )}
    </DataCard>
  );

  const breadcrumbsData = {
    path: [
      [t("video"), videoPath],
    ],
    current: title,
  };
  return (
    <Page seoTitle={title} seoDescription={""} seoImage={
      ytvideo.og_image ? (
        ytvideo.og_image.localFile.publicURL
      ) : (
        ytvideo.squared_photo
          ? ytvideo.squared_photo.localFile.publicURL
          : data.headerImg.publicURL
      )
    }>
      <SinglePageTemplate
        variant="publication"
        header={<Breadcrumbs data={breadcrumbsData} />}
        sidebar={<><YtvideoDataCard /></>}
        mainContent={
          <>
            <h1 data-typesense-field={`title_${language}`}>{title}</h1>
            <ResponsivePlayer videoid={ytvideo.youtubeid} />
            {body && (
              <RichText
                data={body}
                source="markdown"
                data-typesense-field={`body_${language}`}
              />
            )}
            <ShareButtons t={t} url={location.href} text={title} />
            <Cta
              flexWidth
              link="/videos/archive"
              title={t("videos.cta.title")}
              description={t("videos.cta.description")}
            />
          </>
        }
      />
      { ytvideo.noticias && ytvideo.noticias.length > 0 && ( <>
        <h2>{t("news.relatedNews")}</h2>
        <PostGrid
          postCards={
            <>
              {ytvideo.noticias.map((node) => {
                return (
                  <PostCard
                    img={node.principal_photo != null ? getImage(node.principal_photo.localFile.childImageSharp): null}
                    type={t("news.type")}
                    date={node[`Fecha_${language}`]}
                    title={node[`Titular_${language}`]}
                    description={node[`Entradilla_${language}`]}
                    link={`${newsPath}/${node.slug}`}
                    key={uuidv4()}
                  />
                );
              })}
            </>
          }
        />
      </>)}
      { ytvideo.eventos && ytvideo.eventos.length > 0 && ( <>
        <h2>{t("nextEvents.relatedEvents")}</h2>
        <PostGrid
          postCards={
            <>
              {ytvideo.eventos.map((node) => {
                return (
                  <PostCard
                    img={node.principal_photo != null ? getImage(node.principal_photo.localFile.childImageSharp): null}
                    type={t("nextEvents.type")}
                    date={node.Horario[0][`Fecha_${language}`]}
                    title={node[`Titulo_${language}`]}
                    link={`${eventsRootPath}/${node.slug}`}
                    key={uuidv4()}
                  />
                );
              })}
            </>
          }
        />
      </>)}
      <p hidden data-typesense-field="category">
        {t("video")}
      </p>
      <p hidden data-typesense-field="date">
        {Date.parse(originalDateData)}
      </p>
      <p hidden data-typesense-field="link">
        {`${videoPath}/${slug}`}
      </p>
    </Page>
  );
};

export default VideoTemplate;

export const query = graphql`
  query ($language: String!, $id: Int!) {
    locales: allLocale(filter: { language: { eq: $language } }) {
      edges {
        node {
          ...LocalesCommon
        }
      }
    }
    ytvideo: strapiYtvideo(strapi_id: { eq: $id }) {
      ...YtvideoCommon
      descripcion_es
      descripcion_en
      descripcion_gl
      og_image{
        localFile {
          publicURL
        }
      }
      datetime_en: datetime(formatString: "LL", locale: "en")
      datetime_es: datetime(formatString: "LL", locale: "es")
      datetime_gl: datetime(formatString: "LL", locale: "gl")
      ks_people{
        preferred_name {
          family_name
          given_name
        }
        slug
      }
      noticias {
        id
        slug
        Titular_en
        Titular_es
        Titular_gl
        Fecha
        Fecha_en: Fecha(formatString: "LL", locale: "en")
        Fecha_es: Fecha(formatString: "LL", locale: "es")
        Fecha_gl: Fecha(formatString: "LL", locale: "gl")
        Entradilla_en
        Entradilla_es
        Entradilla_gl
        principal_photo {
          localFile {
            publicURL
            childImageSharp {
              gatsbyImageData
            }
          }
          alternativeText
        }
      }
      eventos{
        id
        slug
        Titulo_es
        Titulo_en
        Titulo_gl
        Horario {
          Fecha
          Fecha_en: Fecha(formatString: "dddd, LL", locale: "en")
          Fecha_es: Fecha(formatString: "dddd, LL", locale: "es")
          Fecha_gl: Fecha(formatString: "dddd, LL", locale: "gl")
          Hora_inicio
          Hora_fin
        }
        principal_photo {
          localFile {
            childImageSharp {
              gatsbyImageData(width: 1440)
            }
          }
        }
      }
    }
  }
`;

// Pending project
